import React, { useState, useEffect, useRef, useContext } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link, useLocation } from "react-router-dom";
import { GoogleLogin, googleLogout } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode"; // Corrected import
import { registerUser } from "../utils/userRegistration";
import validateUser from "../utils/getUserLogin";
import emailRegistration from "../utils/emailRegistration";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai"; // Import eye icons
import { UserContext } from "../utils/UserContext";
import { LuShoppingCart } from "react-icons/lu";
import { BsBoxSeam } from "react-icons/bs";
import PhoneNumberModal from "./PhoneNumberModal";
import { updateUserPassword } from "../utils/updateUserPassword";
import { v4 as uuidv4 } from "uuid";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Products" },
  { name: "Solutions" },
  { name: "About Us", href: "/about-us" },
  { name: "Media", href: "/media" },
  { name: "Connect", href: "/connect" },
];

const solutionsDropdown = [
  { name: "Education", href: "/solutions/education" },
  { name: "Corporate", href: "/solutions/corporate" },
];

// Main products dropdown
const productsDropdown = [
  { name: "Interactive Flat Panel" },
  { name: "Active LED Video Wall" },
  { name: "Commercial Display" }, // We'll add a sub-menu for this
];

// Sub-dropdown for Interactive Flat Panel
const interactiveFlatPanelDropdown = [
  {
    name: "Evoke Series",
    href: "/products/interactive-flat-panel/evoke-series",
  },
  { name: "Rise Series", href: "/products/interactive-flat-panel/rise-series" },
  {
    name: "Inspire Series",
    href: "/products/interactive-flat-panel/inspire-series",
  },
];

// Sub-dropdown for Active LED
const activeLedDropdown = [
  { name: "Inertia Series", href: "/products/active-led/inertia-series" },
];

// Sub-dropdown for Commercial Display
const commercialDropdown = [
  {
    name: "Vista Series",
    href: "/products/commercial-display/vista-series",
  },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // Controls which top-level menu is open
  const [interactiveDropdownOpen, setInteractiveDropdownOpen] = useState(false);
  const [activeLedDropdownOpen, setActiveLedDropdownOpen] = useState(false);
  // NEW state to manage Commercial Display dropdown
  const [commercialDropdownOpen, setCommercialDropdownOpen] = useState(false);

  const location = useLocation();

  const [isSignUp, setIsSignUp] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    userName: "",
    email: "",
    phoneNo: "",
    password: "",
    organizationName: "",
    address: "",
    newPassword: "", // new field for forgot password
  });
  const [avatarDropdownOpen, setAvatarDropdownOpen] = useState(false);
  const [isUserPassword, setUserPassword] = useState(false);
  const timerRef = useRef(null);
  const [toast, setToast] = useState({
    showToast: false,
    message: "",
    type: "",
  });
  const {
    user,
    setUser,
    isSignInModalOpen,
    openSignInModal,
    closeSignInModal,
  } = useContext(UserContext);

  // Local state for showing the Phone Number Modal
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  // NEW state to hold the random image object
  const [randomImage, setRandomImage] = useState(null);
  const [randomMobileImage, setRandomMobileImage] = useState(null);
  const [signupImage, setSignUpImage] = useState(null);

  // When the sign-in modal opens, select a random image from the two provided
  useEffect(() => {
    if (isSignInModalOpen) {
      const images = [
        {
          src: "https://content.qeeb.in/coltec/enjoy-exclusive-savings-sign-in.webp",
          // Extract alt text from the file name (before ".webp") and replace hyphens with spaces
          alt: "enjoy exclusive savings sign in",
        },
        {
          src: "https://content.qeeb.in/coltec/e-book-sign-in.webp",
          alt: "e book sign in",
        },
        {
          src: "https://content.qeeb.in/coltec/testimonail-sign-in-popup.webp",
          alt: "testimonail sign in",
        },
      ];
      const signup_images = [
        {
          src: "https://content.qeeb.in/coltec/enjoy-exclusive-savings-sign-up.webp",
          // Extract alt text from the file name (before ".webp") and replace hyphens with spaces
          alt: "enjoy exclusive savings sign in",
        },
        {
          src: "https://content.qeeb.in/coltec/e-book-sign-up.webp",
          alt: "e book sign in",
        },
        {
          src: "https://content.qeeb.in/coltec/testimonail-sign-up-popup.webp",
          alt: "testimonail sign up",
        },
      ];
      const mobile_images = [
        {
          src: "https://content.qeeb.in/coltec/enjoy-exclusive-savings-mobile-sign-in.webp",
          // Extract alt text from the file name (before ".webp") and replace hyphens with spaces
          alt: "enjoy exclusive savings sign in",
        },
        {
          src: "https://content.qeeb.in/coltec/e-book-sign-in-mobile-img.webp",
          alt: "e book sign in",
        },
        {
          src: "https://content.qeeb.in/coltec/testimonail-sign-in-mobile-img.webp",
          alt: "testimonail sign up",
        },
      ];
      setRandomImage(images[Math.floor(Math.random() * images.length)]);
      setRandomMobileImage(
        mobile_images[Math.floor(Math.random() * mobile_images.length)]
      );
      setSignUpImage(
        signup_images[Math.floor(Math.random() * signup_images.length)]
      );
    }
  }, [isSignInModalOpen]);

  // Handle top-level menu clicks
  const handleMenuClick = (menuName) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
    // Close all submenus when a new top-level menu is opened
    setInteractiveDropdownOpen(false);
    setActiveLedDropdownOpen(false);
    setCommercialDropdownOpen(false);
  };

  // Handle Interactive sub-dropdown
  const handleInteractiveClick = () => {
    setInteractiveDropdownOpen(!interactiveDropdownOpen);
    // Close sibling sub-dropdowns if open
    if (activeLedDropdownOpen) setActiveLedDropdownOpen(false);
    if (commercialDropdownOpen) setCommercialDropdownOpen(false);
  };

  // Handle Active LED sub-dropdown
  const handleActiveLedClick = () => {
    setActiveLedDropdownOpen(!activeLedDropdownOpen);
    // Close sibling sub-dropdowns if open
    if (interactiveDropdownOpen) setInteractiveDropdownOpen(false);
    if (commercialDropdownOpen) setCommercialDropdownOpen(false);
  };

  // Handle Commercial Display sub-dropdown
  const handleCommercialClick = () => {
    setCommercialDropdownOpen(!commercialDropdownOpen);
    // Close sibling sub-dropdowns if open
    if (interactiveDropdownOpen) setInteractiveDropdownOpen(false);
    if (activeLedDropdownOpen) setActiveLedDropdownOpen(false);
  };

  // New useEffect: Open the sign-in modal every 6 seconds if the user is not logged in
  useEffect(() => {
    if (!user) {
      const interval = setInterval(() => {
        openSignInModal();
      }, 10000); // 10000ms = 10 seconds
      return () => clearInterval(interval);
    }
  }, [user, openSignInModal]);

  useEffect(() => {
    if (isSignInModalOpen && timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [isSignInModalOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsFixed(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location]);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, [setUser]);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  // Google Auth success handler
  const handleGoogleSuccess = async (credentialResponse) => {
    if (credentialResponse.credential) {
      try {
        const decoded = jwtDecode(credentialResponse.credential);
        console.log("Google User:", decoded);
        const pagePath = location.pathname;
        // Call your API which returns the user details including userId
        const emailUser = await emailRegistration(
          decoded.email,
          decoded.name,
          pagePath
        );
        console.log(emailUser, "emailUser");

        // Store the userId from the API response in localStorage
        if (emailUser && emailUser.userId) {
          localStorage.setItem("userId", emailUser.userId);
          localStorage.setItem("sessionid", uuidv4());
        }

        // Retrieve updated phone number from localStorage, if any
        const storedPhoneNo = localStorage.getItem("userPhoneNo") || "";

        const newUser = {
          name: decoded.name,
          email: decoded.email,
          avatar: decoded.picture,
          loginMethod: "google",
          phoneNo: storedPhoneNo,
          // Optionally, add userId to your user state as well
          userId: emailUser.userId,
        };

        setUser(newUser);
        localStorage.setItem("user", JSON.stringify(newUser));
        localStorage.setItem("email", decoded.email);

        closeSignInModal();
        setIsSignUp(false);
        setCurrentStep(1);
        setFormData({
          userName: "",
          email: "",
          phoneNo: "",
          password: "",
          organizationName: "",
          address: "",
        });
      } catch (error) {
        console.error("Error:", error);
      }
    }
  };

  const handleGoogleError = () => {
    console.error("Google Sign-In was unsuccessful. Try again later.");
  };

  const handleLogout = () => {
    googleLogout();
    setUser(null);
    setAvatarDropdownOpen(false);
    localStorage.removeItem("email");
    localStorage.removeItem("userId");
    localStorage.removeItem("sessionid");
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle Sign-Up form submission
  const handleSignUpSubmit = async (e) => {
    e.preventDefault();

    if (currentStep === 1) {
      const { userName, email, phoneNo, password } = formData;
      if (!userName || !email || !phoneNo || !password) {
        setToast({
          showToast: true,
          message: "Please fill in all required fields.",
          type: "error",
        });
        setTimeout(
          () => setToast({ showToast: false, message: "", type: "" }),
          2000
        );
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      const { organizationName, address } = formData;
      if (!organizationName || !address) {
        setToast({
          showToast: true,
          message: "Please fill in all required fields.",
          type: "error",
        });
        setTimeout(
          () => setToast({ showToast: false, message: "", type: "" }),
          2000
        );
        return;
      }
      try {
        const response = await registerUser(formData);
        if (response.status === 1) {
          setToast({
            showToast: true,
            message: "User Registered Successfully.",
            type: "success",
          });

          setTimeout(() => {
            setToast({ showToast: false, message: "", type: "" });
            openSignInModal();
          }, 2000);
        } else {
          setToast({
            showToast: true,
            message: "User registration failed. Please try again.",
            type: "error",
          });
          setTimeout(
            () => setToast({ showToast: false, message: "", type: "" }),
            2000
          );
        }
        closeSignInModal();
        setIsSignUp(false);
        setCurrentStep(1);
        setFormData({
          userName: "",
          email: "",
          phoneNo: "",
          password: "",
          organizationName: "",
          address: "",
        });
      } catch (error) {
        alert("User registration failed. Please try again.");
      }
    }
  };

  // Handle Sign-In form submission
  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    if (forgotPassword) {
      // Forgot password flow: use email and newPassword fields
      const { email, newPassword } = formData;
      if (!email || !newPassword) {
        setToast({
          showToast: true,
          message: "Please fill in both email and new password.",
          type: "error",
        });
        setTimeout(
          () => setToast({ showToast: false, message: "", type: "" }),
          2000
        );
        return;
      }
      try {
        const response = await updateUserPassword(email, newPassword);
        if (response.status === 1) {
          setToast({
            showToast: true,
            message: "Password updated successfully.",
            type: "success",
          });
          setTimeout(() => {
            setToast({ showToast: false, message: "", type: "" });
            closeSignInModal();
            setForgotPassword(false);
            // Clear out the relevant fields
            setFormData({
              ...formData,
              email: "",
              newPassword: "",
            });
          }, 2000);
        } else {
          setToast({
            showToast: true,
            message: response.message,
            type: "error",
          });
          setTimeout(
            () => setToast({ showToast: false, message: "", type: "" }),
            2000
          );
        }
      } catch (error) {
        setToast({
          showToast: true,
          message: "Error updating password. Please try again.",
          type: "error",
        });
        setTimeout(
          () => setToast({ showToast: false, message: "", type: "" }),
          2000
        );
      }
      return;
    }

    const { email, password } = formData;
    if (!email || !password) {
      setToast({
        showToast: true,
        message: "Please fill in both email and password.",
        type: "error",
      });
      setTimeout(
        () => setToast({ showToast: false, message: "", type: "" }),
        2000
      );
      return;
    }

    try {
      const currentPage = location.pathname;
      const userData = await validateUser(email, password, currentPage);

      if (userData.status === 1) {
        localStorage.setItem("userId", userData.userId);
        localStorage.setItem("sessionid", uuidv4());
        setToast({
          showToast: true,
          message: "Sign-In Successful!",
          type: "success",
        });
        setTimeout(
          () => setToast({ showToast: false, message: "", type: "" }),
          2000
        );
        setUser({
          name: userData.userName,
          email: userData.email,
          loginMethod: "manual",
        });
        localStorage.setItem("email", userData.email);
        closeSignInModal();
        setIsSignUp(false);
        setFormData({ email: "", password: "" });
      } else {
        setToast({
          showToast: true,
          message: "Invalid credentials, please try again.",
          type: "error",
        });
        setTimeout(
          () => setToast({ showToast: false, message: "", type: "" }),
          2000
        );
      }
    } catch (error) {
      setToast({
        showToast: true,
        message: "Error during sign-in. Please try again.",
        type: "error",
      });
      setTimeout(
        () => setToast({ showToast: false, message: "", type: "" }),
        2000
      );
    }
  };

  // PHONE MODAL: Show the phone number modal only if the user is Google-authenticated
  // and the phone number hasn't been updated yet.
  useEffect(() => {
    if (
      user &&
      user.loginMethod === "google" &&
      !localStorage.getItem("phoneNumberUpdated")
    ) {
      const timer = setTimeout(() => {
        setShowPhoneModal(true);
      }, 10000); // 10 seconds delay

      return () => clearTimeout(timer);
    } else {
      setShowPhoneModal(false);
    }
  }, [location, user]);

  return (
    <>
      <header
        className={`header-menu inset-x-0 top-0 z-50 border-white/30 bg-gradient-radial backdrop-blur-md border-b ${
          isFixed ? "fixed fadeInDown bg-white fixed-nav" : "absolute nav-text"
        }`}
      >
        <nav
          aria-label="Global"
          className="flex items-center lg:justify-center justify-between px-5 lg:px-8 max-w-7xl m-auto"
        >
          <div className="flex lg:flex-1 ml-[-13px] md:ml-0 py-3.5">
            <Link to="/">
              <img
                alt="Coltec Logo"
                width="130"
                height="24"
                src={
                  isFixed
                    ? "https://content.qeeb.in/coltec/coltec-logo-black.svg"
                    : "https://content.qeeb.in/coltec/coltec-logo-white.svg"
                }
                className="md:h-7 h-[22px]"
              />
            </Link>
          </div>
          <div className="flex lg:hidden">
            <div className="flex space-x-3 items-center">
              <div className="bg-white hover:bg-orange-400 border border-gray-200 hover:border-orange-400 text-gray-800 text-sm px-2 py-[6px] rounded-lg shadow-lg hover:translate-y-[-2px] transition-all ease-linear duration-300">
                <Link to="/shop" className="flex space-x-2">
                  <img
                    src="https://content.qeeb.in/coltec/products/shop-icon.svg"
                    alt="cart-icon"
                    title="Shop Now"
                    className="h-[18px] invert"
                  />
                </Link>
              </div>
              {user ? (
                <div className="relative lg:pr-0 pr-3">
                  {user.loginMethod === "google" ? (
                    <button
                      className="flex items-center space-x-2 text-sm rounded-full transition-all ease-linear duration-300"
                      onClick={() => setAvatarDropdownOpen(!avatarDropdownOpen)}
                    >
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="h-8 w-8 rounded-full"
                      />
                    </button>
                  ) : (
                    <button
                      className="bg-white border border-[#9771FF] duration-300 ease-linear flex h-9 w-9 md:h-10 items-center justify-center p-1 rounded-full space-x-2 transition-all md:w-10"
                      onClick={() => setAvatarDropdownOpen(!avatarDropdownOpen)}
                    >
                      <img
                        src="https://content.qeeb.in/coltec/default-avatar.png"
                        alt={user.userName}
                        className="md:h-8 md:w-8 w-7 h-7 rounded-full"
                      />
                    </button>
                  )}
                  {avatarDropdownOpen && (
                    <div className="absolute right-0 mt-4 border border-gray-200 w-40 bg-white rounded-md shadow-lg p-3 z-20">
                      <Link
                        to="/products/cart"
                        className="flex text-sm font-medium mb-3 group"
                      >
                        <LuShoppingCart className="text-lg mr-2" />
                        <span className="group-hover:text-blue-500">
                          View cart
                        </span>
                      </Link>
                      <Link
                        to="/products/orders"
                        className="flex text-sm font-medium mb-3 group"
                      >
                        <BsBoxSeam className="text-lg mr-2" />
                        <span className="group-hover:text-blue-500">
                          Your Orders
                        </span>
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setAvatarDropdownOpen(false);
                        }}
                        className="flex items-center text-left text-sm font-medium text-gray-800 hover:text-red-600"
                      >
                        <img
                          src="https://content.qeeb.in/coltec/products/logout-icon.svg"
                          alt="Logout"
                          className="h-[14px] mr-2"
                        />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="pr-3 lg:pr-0">
                  <button
                    onClick={() => {
                      openSignInModal();
                      setIsSignUp(false);
                      setCurrentStep(1);
                      setFormData({
                        name: "",
                        email: "",
                        mobileNumber: "",
                        password: "",
                        originationName: "",
                        location: "",
                      });
                    }}
                    className="bg-white hover:bg-blue-600 border border-gray-200 hover:border-blue-600 text-gray-800 hover:text-white text-sm px-2 py-[6px] rounded-lg shadow-lg transition-all ease-linear duration-300"
                  >
                    <img
                      src="https://content.qeeb.in/coltec/default-avatar.png"
                      alt="User-Icon"
                      className="h-[19px] rounded-full"
                    />
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className={`-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 ${
                isFixed ? "text-gray-400" : "text-white"
              }`}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="h-8 w-8" />
            </button>
          </div>
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center lg:gap-x-12">
            {navigation.map((item) =>
              item.name === "Solutions" ? (
                <div key={item.name} className="relative">
                  <button
                    onClick={() => handleMenuClick("solutions")}
                    className="text-sm transition-all link font-medium leading-6 text-gray-950"
                  >
                    {item.name}
                  </button>
                  {openMenu === "solutions" && (
                    <div className="absolute left-0 right-0 mt-5 w-36 bg-white shadow-lg rounded-md ring-1 ring-gray-900/5 fadeInDown">
                      <div className="py-1 dropdown-link">
                        {solutionsDropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className="block px-4 py-2 text-sm text-gray-950 hover:bg-sky-100 link font-medium"
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : item.name === "Products" ? (
                <div key={item.name} className="relative">
                  <button
                    onClick={() => handleMenuClick("products")}
                    className="text-sm transition-all link font-medium leading-6 text-gray-950"
                  >
                    {item.name}
                  </button>
                  {openMenu === "products" && (
                    <div className="absolute left-0 right-0 mt-5 w-48 bg-white shadow-lg rounded-md ring-1 ring-gray-900/5 fadeInDown">
                      <div className="py-1 dropdown-link">
                        {productsDropdown.map((product) => {
                          if (product.name === "Interactive Flat Panel") {
                            return (
                              <div key={product.name} className="relative">
                                <button
                                  onClick={handleInteractiveClick}
                                  className="block relative px-4 py-2 text-sm text-gray-950 hover:bg-sky-100 link font-medium"
                                >
                                  {product.name}{" "}
                                  <img
                                    alt="arrow-right"
                                    src="https://content.qeeb.in/coltec/chevron-right.svg"
                                    className="chevron-right lg:inline-block hidden absolute top-[10px] ml-[5px] w-[15px]"
                                  />
                                </button>
                                {interactiveDropdownOpen && (
                                  <div className="absolute left-full top-0 -mt-1 ml-[2px] w-36 bg-white shadow-lg rounded-md ring-1 ring-gray-900/5 fadeInDown">
                                    <div className="py-1">
                                      {interactiveFlatPanelDropdown.map(
                                        (item) => (
                                          <Link
                                            key={item.name}
                                            to={item.href}
                                            className="block px-4 py-2 text-sm text-gray-950 hover:bg-sky-100 link font-medium"
                                          >
                                            {item.name}
                                          </Link>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          } else if (product.name === "Active LED Video Wall") {
                            return (
                              <div key={product.name} className="relative">
                                <button
                                  onClick={handleActiveLedClick}
                                  className="block relative px-4 py-2 text-sm text-gray-950 hover:bg-sky-100 link font-medium"
                                >
                                  {product.name}{" "}
                                  <img
                                    alt="arrow-right"
                                    src="https://content.qeeb.in/coltec/chevron-right.svg"
                                    className="chevron-right lg:inline-block hidden absolute top-[10px] ml-[5px] w-[15px]"
                                  />
                                </button>
                                {activeLedDropdownOpen && (
                                  <div className="absolute left-full top-0 -mt-1 ml-[2px] w-36 bg-white shadow-lg rounded-md ring-1 ring-gray-900/5 fadeInDown">
                                    <div className="py-1">
                                      {activeLedDropdown.map((item) => (
                                        <Link
                                          key={item.name}
                                          to={item.href}
                                          className="block px-4 py-2 text-sm text-gray-950 hover:bg-sky-100 link font-medium"
                                        >
                                          {item.name}
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          // NEW: Commercial Display Dropdown
                          else if (product.name === "Commercial Display") {
                            return (
                              <div key={product.name} className="relative">
                                <button
                                  onClick={handleCommercialClick}
                                  className="block relative px-4 py-2 text-sm text-gray-950 hover:bg-sky-100 link font-medium"
                                >
                                  {product.name}
                                  <img
                                    alt="arrow-right"
                                    src="https://content.qeeb.in/coltec/chevron-right.svg"
                                    className="chevron-right lg:inline-block hidden absolute top-[10px] ml-[5px] w-[15px]"
                                  />
                                </button>
                                {commercialDropdownOpen && (
                                  <div className="absolute left-full top-0 -mt-1 ml-[2px] w-44 bg-white shadow-lg rounded-md ring-1 ring-gray-900/5 fadeInDown">
                                    <div className="py-1">
                                      {commercialDropdown.map((item) => (
                                        <Link
                                          key={item.name}
                                          to={item.href}
                                          className="block px-4 py-2 text-sm text-gray-950 hover:bg-sky-100 link font-medium"
                                        >
                                          {item.name}
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          } else {
                            return (
                              <Link
                                key={product.name}
                                to={product.href}
                                className="block px-4 py-2 text-sm text-gray-950 hover:bg-sky-100 link font-medium"
                              >
                                {product.name}
                              </Link>
                            );
                          }
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-sm transition-all link font-medium leading-6 text-gray-950"
                >
                  {item.name}
                </Link>
              )
            )}
            <div className="flex space-x-5 items-center">
              <div className="bg-white hover:bg-orange-400 border border-gray-200 hover:border-orange-400 text-gray-800 text-sm px-2 py-[8px] rounded-lg shadow-lg hover:translate-y-[-2px] transition-all ease-linear duration-300">
                <Link to="/shop" className="flex space-x-2">
                  <img
                    src="https://content.qeeb.in/coltec/products/shop-icon.svg"
                    alt="cart-icon"
                    title="Shop Now"
                    className="h-[18px] invert"
                  />
                  <p className="font-medium">Shop</p>
                </Link>
              </div>
              {user ? (
                <div className="relative">
                  {user.loginMethod === "google" ? (
                    <button
                      className="flex items-center space-x-2 text-sm rounded-full transition-all ease-linear duration-300"
                      onClick={() => setAvatarDropdownOpen(!avatarDropdownOpen)}
                    >
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="h-8 w-8 rounded-full"
                      />
                    </button>
                  ) : (
                    <button
                      className="bg-white border border-[#9771FF] duration-300 ease-linear flex h-10 items-center justify-center p-1 rounded-full space-x-2 transition-all w-10"
                      onClick={() => setAvatarDropdownOpen(!avatarDropdownOpen)}
                    >
                      <img
                        src="https://content.qeeb.in/coltec/default-avatar.png"
                        alt={user.userName}
                        className="h-8 w-8 rounded-full"
                      />
                    </button>
                  )}
                  {avatarDropdownOpen && (
                    <div className="absolute right-0 mt-4 border border-gray-200 w-40 bg-white rounded-md shadow-lg p-3 z-20">
                      <Link
                        to="/products/cart"
                        className="flex text-sm font-medium mb-3 group"
                      >
                        <LuShoppingCart className="text-lg mr-2" />
                        <span className="group-hover:text-blue-500">
                          View cart
                        </span>
                      </Link>
                      <Link
                        to="/products/orders"
                        className="flex text-sm font-medium mb-3 group"
                      >
                        <BsBoxSeam className="text-lg mr-2" />
                        <span className="group-hover:text-blue-500">
                          Your Orders
                        </span>
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setAvatarDropdownOpen(false);
                        }}
                        className="flex items-center text-left text-sm font-medium text-gray-800 hover:text-red-600"
                      >
                        <img
                          src="https://content.qeeb.in/coltec/products/logout-icon.svg"
                          alt="Logout"
                          className="h-[14px] mr-2"
                        />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => {
                    openSignInModal();
                    setIsSignUp(false);
                    setCurrentStep(1);
                    setFormData({
                      name: "",
                      email: "",
                      mobileNumber: "",
                      password: "",
                      originationName: "",
                      location: "",
                    });
                  }}
                  className="bg-white hover:bg-blue-600 border border-gray-200 hover:border-blue-600 text-gray-800 hover:text-white text-sm px-5 py-[8px] rounded-lg shadow-lg transition-all ease-linear duration-300"
                >
                  Sign in
                </button>
              )}
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        <Dialog
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          className="lg:hidden"
        >
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-white/10">
            <div className="flex items-center justify-between">
              <Link to="/">
                <img
                  alt="Logo"
                  src="https://content.qeeb.in/coltec/coltec-logo-black.svg"
                  className="h-5 w-auto"
                />
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-md text-gray-400"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/25">
                <div className="flex flex-col py-6">
                  {navigation.map((item) =>
                    item.name === "Solutions" ? (
                      <div key={item.name} className="relative">
                        <button
                          onClick={() => handleMenuClick("mobile-solutions")}
                          className="-mx-3 block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                        >
                          {item.name}
                        </button>
                        {openMenu === "mobile-solutions" && (
                          <div>
                            {solutionsDropdown.map((dropdownItem) => (
                              <Link
                                key={dropdownItem.name}
                                to={dropdownItem.href}
                                className="block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                              >
                                {dropdownItem.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : item.name === "Products" ? (
                      <div key={item.name} className="relative">
                        <button
                          onClick={() => handleMenuClick("mobile-products")}
                          className="-mx-3 block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                        >
                          {item.name}
                        </button>
                        {openMenu === "mobile-products" && (
                          <div>
                            {productsDropdown.map((product) => {
                              if (product.name === "Interactive Flat Panel") {
                                return (
                                  <div key={product.name} className="relative">
                                    <button
                                      onClick={handleInteractiveClick}
                                      className="block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                                    >
                                      {product.name}
                                    </button>
                                    {interactiveDropdownOpen && (
                                      <div className="ml-4">
                                        {interactiveFlatPanelDropdown.map(
                                          (item) => (
                                            <Link
                                              key={item.name}
                                              to={item.href}
                                              className="block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                                            >
                                              {item.name}
                                            </Link>
                                          )
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              } else if (
                                product.name === "Active LED Video Wall"
                              ) {
                                return (
                                  <div key={product.name} className="relative">
                                    <button
                                      onClick={handleActiveLedClick}
                                      className="block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                                    >
                                      {product.name}
                                    </button>
                                    {activeLedDropdownOpen && (
                                      <div className="ml-4">
                                        {activeLedDropdown.map((item) => (
                                          <Link
                                            key={item.name}
                                            to={item.href}
                                            className="block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                                          >
                                            {item.name}
                                          </Link>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              // Mobile menu for Commercial Display
                              else if (product.name === "Commercial Display") {
                                return (
                                  <div key={product.name} className="relative">
                                    <button
                                      onClick={handleCommercialClick}
                                      className="block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                                    >
                                      {product.name}
                                    </button>
                                    {commercialDropdownOpen && (
                                      <div className="ml-4">
                                        {commercialDropdown.map((item) => (
                                          <Link
                                            key={item.name}
                                            to={item.href}
                                            className="block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                                          >
                                            {item.name}
                                          </Link>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              } else {
                                return (
                                  <Link
                                    key={product.name}
                                    to={product.href}
                                    className="block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                                  >
                                    {product.name}
                                  </Link>
                                );
                              }
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="-mx-3 block rounded-lg px-3 py-2 text-sm font-medium leading-7 text-black"
                      >
                        {item.name}
                      </Link>
                    )
                  )}
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      {/* Sign-In / Sign-Up Modal */}
      {isSignInModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end md:items-center justify-center h-svh md:min-h-screen px-4 py-6">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => {
                closeSignInModal();
                setIsSignUp(false);
                setCurrentStep(1);
                setFormData({
                  userName: "",
                  email: "",
                  phoneNo: "",
                  password: "",
                  organizationName: "",
                  address: "",
                  newPassword: "",
                });
              }}
            >
              <div className="absolute inset-0 bg-black opacity-50"></div>
            </div>
            <div
              className={`bg-gradient-to-br from-blue-100 to-slate-50 animate-zoom-in animate-slide-up rounded-2xl shadow-xl transform transition-all md:max-w-[720px] lg:max-w-[750px] w-full z-10`}
            >
              <div className="grid md:grid-cols-2 grid-cols-1 gap-0 relative">
                {/* Right Side Image (Sign-In & Step 2) */}
                {!isSignUp ? (
                  <>
                    <div className="relative p-4 hidden md:block">
                      <div className="h-full">
                        {randomImage && (
                          <img
                            alt={randomImage.alt}
                            src={randomImage.src}
                            className="md:h-full h-[130px] object-fill md:object-fill w-full"
                          />
                        )}
                      </div>
                    </div>
                    <div className="relative block md:hidden">
                      {randomMobileImage && (
                        <img
                          alt={randomMobileImage.alt}
                          src={randomMobileImage.src}
                          className="md:h-full h-[150px] object-fill md:object-fill w-full"
                        />
                      )}
                    </div>
                  </>
                ) : null}
                {/* Left Side Form */}
                {isSignUp ? (
                  <>
                    <div className="relative block md:hidden">
                      <div className="h-full">
                        {randomMobileImage && (
                          <img
                            alt={randomMobileImage.alt}
                            src={randomMobileImage.src}
                            className="md:h-full h-[150px] object-fill md:object-fill w-full"
                          />
                        )}
                      </div>
                    </div>
                  </>
                ) : null}
                <div
                  className={`bg-gradient-to-br from-blue-100 to-slate-50 md:p-8 p-5  ${
                    !isSignUp
                      ? "lg:rounded-r-[20px] lg:rounded-none rounded-[20px]"
                      : "lg:rounded-l-[20px] lg:rounded-none rounded-[20px]"
                  }`}
                >
                  <div className="bg-[#DDEAFF] w-[30px] h-[30px] rounded-full flex justify-center items-center absolute right-[-10px] z-[1] top-[-10px]">
                    <button
                      onClick={() => {
                        closeSignInModal();
                        setIsSignUp(false);
                        setCurrentStep(1);
                        setFormData({
                          userName: "",
                          email: "",
                          phoneNo: "",
                          password: "",
                          organizationName: "",
                          address: "",
                          newPassword: "",
                        });
                      }}
                    >
                      <img
                        alt="close-icon"
                        className="h-5"
                        src="https://content.qeeb.in/coltec/close-icon.svg"
                      />
                    </button>
                  </div>
                  <div className="mx-auto w-full">
                    {!isSignUp ? (
                      // Sign-In View
                      <div>
                        <div className="md:mt-4">
                          <p className="text-center text-[#b2b1b1] text-xs font-normal leading-[14.40px]">
                            Sign-in with your Google account
                          </p>
                          <div className="mt-4 mb-6 flex justify-center">
                            <GoogleLogin
                              onSuccess={handleGoogleSuccess}
                              onError={handleGoogleError}
                              useOneTap
                              text="signin_with"
                              theme="filled_blue"
                              width="220px"
                              size="large"
                              shape="circle"
                            />
                          </div>
                          <p className="text-center text-[#bababa] text-xs font-normal leading-[14.40px]">
                            Or use your email to sign-in
                          </p>
                          <form className="mt-5" onSubmit={handleSignInSubmit}>
                            <div className="grid grid-cols-1">
                              <div className="relative">
                                <input
                                  type="email"
                                  name="email"
                                  placeholder="Email"
                                  value={formData.email}
                                  onChange={handleInputChange}
                                  className="w-full h-10 mb-4 text-sm pl-8 bg-zinc-100 rounded-lg border-0 placeholder:text-sm"
                                  required
                                />
                                <img
                                  alt="email-icon"
                                  src="https://content.qeeb.in/coltec/home/email-icon.svg"
                                  className="w-[13px] h-[13px] absolute top-[14px] left-3"
                                />
                              </div>
                              {forgotPassword ? (
                                <div className="relative">
                                  <input
                                    type={isUserPassword ? "text" : "password"}
                                    name="newPassword"
                                    placeholder="Enter new password"
                                    value={formData.newPassword}
                                    onChange={handleInputChange}
                                    className="w-full text-sm h-10 pl-8 bg-zinc-100 rounded-lg border-0 placeholder:text-sm"
                                    required
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setUserPassword(!isUserPassword)
                                    }
                                    className="absolute bottom-2.5 right-4 flex items-center text-[#8A9CB5] hover:text-[#8A9CB5]/95"
                                  >
                                    {isUserPassword ? (
                                      <AiOutlineEyeInvisible size={20} />
                                    ) : (
                                      <AiOutlineEye size={20} />
                                    )}
                                  </button>
                                  <img
                                    alt="password-icon"
                                    src="https://content.qeeb.in/coltec/home/password-icon.svg"
                                    className="w-[14px] h-[14px] absolute top-[12px] left-3"
                                  />
                                </div>
                              ) : (
                                <div className="relative">
                                  <input
                                    type={isUserPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="w-full text-sm h-10 pl-8 bg-zinc-100 rounded-lg border-0 placeholder:text-sm"
                                    required
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setUserPassword(!isUserPassword)
                                    }
                                    className="absolute bottom-2.5 right-4 flex items-center text-[#8A9CB5] hover:text-[#8A9CB5]/95"
                                  >
                                    {isUserPassword ? (
                                      <AiOutlineEyeInvisible size={20} />
                                    ) : (
                                      <AiOutlineEye size={20} />
                                    )}
                                  </button>
                                  <img
                                    alt="password-icon"
                                    src="https://content.qeeb.in/coltec/home/password-icon.svg"
                                    className="w-[14px] h-[14px] absolute top-[12px] left-3"
                                  />
                                </div>
                              )}
                              {/* Link to toggle forgot password mode */}
                              <div className="text-right mt-2">
                                {forgotPassword ? (
                                  <p className="text-[11px] text-gray-600">
                                    Remember your password?{" "}
                                    <button
                                      onClick={() => {
                                        setForgotPassword(false);
                                        // Optionally clear the newPassword field
                                        setFormData({
                                          ...formData,
                                          newPassword: "",
                                        });
                                      }}
                                      className="text-blue-600 hover:underline"
                                    >
                                      Sign in
                                    </button>
                                  </p>
                                ) : (
                                  <p className="text-[11px] text-gray-600">
                                    Forgot password?{" "}
                                    <button
                                      onClick={() => {
                                        setForgotPassword(true);
                                        // Clear the normal password field
                                        setFormData({
                                          ...formData,
                                          password: "",
                                        });
                                      }}
                                      className="text-blue-600 hover:underline"
                                    >
                                      Click here
                                    </button>
                                  </p>
                                )}
                              </div>
                              <p className="text-[#232323] text-[11px] font-normal mt-2">
                                By continuing, you agree to COLTEC's{" "}
                                <Link
                                  to="/privacy-policy"
                                  className="text-[#4282ff]"
                                >
                                  Privacy Policy
                                </Link>{" "}
                                and{" "}
                                <Link
                                  to="/cookies-policy"
                                  className="text-[#4282ff]"
                                >
                                  Cookies Policy
                                </Link>
                                .
                              </p>
                              <div className="text-center mt-4">
                                <button className="bg-[#4282ff] text-white py-2 px-10 rounded-full">
                                  {forgotPassword ? "Rest Password" : "Submit"}
                                </button>
                              </div>
                            </div>
                          </form>

                          <div className="mt-3 text-center">
                            <p className="text-[11px] text-gray-600">
                              Don't have an account?{" "}
                              <button
                                onClick={() => {
                                  setIsSignUp(true);
                                  setCurrentStep(1);
                                  setFormData({
                                    userName: "",
                                    email: "",
                                    phoneNo: "",
                                    password: "",
                                    organizationName: "",
                                    address: "",
                                  });
                                }}
                                className="text-blue-600 hover:underline"
                              >
                                Sign Up
                              </button>
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Sign-Up View (Multi-Step)
                      <div>
                        <></>
                        <div>
                          <h2 className="text-center text-[#3b82f6] text-[28px] md:text-[32px] font-semibold leading-[38.40px] mb-5">
                            Sign-up
                          </h2>
                          <form className="mt-5" onSubmit={handleSignUpSubmit}>
                            {currentStep === 1 && (
                              <div className="grid grid-cols-1 gap-3">
                                <div className="relative">
                                  <input
                                    type="text"
                                    name="userName"
                                    placeholder="Name"
                                    value={formData.userName}
                                    onChange={handleInputChange}
                                    className="w-full text-sm h-10 pl-8 bg-zinc-100 rounded-lg border-0 placeholder:text-sm"
                                    required
                                  />
                                  <img
                                    alt="name-icon"
                                    src="https://content.qeeb.in/coltec/home/name-icon.svg"
                                    className="w-[13px] h-[13px] absolute top-[13px] left-3"
                                  />
                                </div>
                                <div className="relative">
                                  <input
                                    type="email"
                                    name="email"
                                    placeholder="Email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="w-full text-sm h-10 pl-8 bg-zinc-100 rounded-lg border-0 placeholder:text-sm"
                                    required
                                  />
                                  <img
                                    alt="email-icon"
                                    src="https://content.qeeb.in/coltec/home/email-icon.svg"
                                    className="w-[13px] h-[13px] absolute top-[13px] left-3"
                                  />
                                </div>
                                <div className="relative">
                                  <input
                                    type="tel"
                                    name="mobileNumber"
                                    placeholder="Mobile Number"
                                    value={formData.phoneNo}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /[^0-9]/g,
                                        ""
                                      );
                                      setFormData({
                                        ...formData,
                                        phoneNo: value,
                                      });
                                    }}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className="w-full text-sm h-10 pl-8 bg-zinc-100 rounded-lg border-0 placeholder:text-sm"
                                    required
                                  />
                                  <img
                                    alt="phone-icon"
                                    src="https://content.qeeb.in/coltec/home/phone-icon.svg"
                                    className="w-[13px] h-[13px] absolute top-[13px] left-3"
                                  />
                                </div>
                                <div className="relative">
                                  <input
                                    type={isUserPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="w-full text-sm h-10 pl-8 bg-zinc-100 rounded-lg border-0 placeholder:text-sm"
                                    required
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setUserPassword(!isUserPassword)
                                    }
                                    className="absolute bottom-2.5 right-4 flex items-center text-[#8A9CB5] hover:text-[#8A9CB5]/95"
                                  >
                                    {isUserPassword ? (
                                      <AiOutlineEyeInvisible size={20} />
                                    ) : (
                                      <AiOutlineEye size={20} />
                                    )}
                                  </button>
                                  <img
                                    alt="password-icon"
                                    src="https://content.qeeb.in/coltec/home/password-icon.svg"
                                    className="w-[14px] h-[14px] absolute top-[12px] left-3"
                                  />
                                </div>
                              </div>
                            )}
                            {currentStep === 2 && (
                              <div className="grid grid-cols-1 mt-8 gap-4">
                                <div className="relative">
                                  <input
                                    type="text"
                                    name="organizationName"
                                    placeholder="Organization Name"
                                    value={formData.organizationName}
                                    onChange={handleInputChange}
                                    className="w-full text-sm h-10 pl-8 bg-zinc-100 rounded-lg border-0 placeholder:text-sm"
                                    required
                                  />
                                  <img
                                    alt="company-icon"
                                    src="https://content.qeeb.in/coltec/home/company-icon.svg"
                                    className="w-[15px] h-[15px] absolute top-[12px] left-3"
                                  />
                                </div>
                                <div className="relative">
                                  <input
                                    type="text"
                                    name="address"
                                    placeholder="Location"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    className="w-full text-sm h-10 pl-8 bg-zinc-100 rounded-lg border-0 placeholder:text-sm"
                                    required
                                  />
                                  <img
                                    alt="location-icon"
                                    src="https://content.qeeb.in/coltec/home/location-icon.svg"
                                    className="w-[15px] h-[15px] absolute top-[12px] left-3"
                                  />
                                </div>
                              </div>
                            )}
                            <div className="flex justify-between mt-5">
                              {currentStep === 2 && (
                                <button
                                  type="button"
                                  onClick={() => setCurrentStep(1)}
                                  className="bg-gray-300 text-gray-700 px-3 py-2 mr-4 rounded-md hover:bg-gray-400 transition-colors"
                                >
                                  <img
                                    alt="Arrow Black"
                                    src="https://content.qeeb.in/coltec/arrow-black.svg"
                                    className="w-5"
                                  />
                                </button>
                              )}
                              <button
                                type="submit"
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full w-full transition-colors"
                              >
                                {currentStep === 1 ? "Submit" : "Sign Up"}
                              </button>
                            </div>
                          </form>
                          <div className="flex justify-center flex-col items-center">
                            <p className="text-center my-3 text-[#bababa] text-xs font-normal leading-[14.40px]">
                              OR
                            </p>
                            <GoogleLogin
                              onSuccess={handleGoogleSuccess}
                              onError={handleGoogleError}
                              useOneTap
                              text="signup_with"
                              width="220px"
                              size="large"
                              shape="circle"
                              theme="filled_blue"
                            />
                          </div>
                          <div className="mt-4 text-center">
                            <p className="text-xs text-gray-600">
                              Already have an account?{" "}
                              <button
                                onClick={() => {
                                  setIsSignUp(false);
                                  setCurrentStep(1);
                                  setFormData({
                                    userName: "",
                                    email: "",
                                    phoneNo: "",
                                    password: "",
                                    organizationName: "",
                                    address: "",
                                  });
                                }}
                                className="text-blue-600 hover:underline"
                              >
                                Sign In
                              </button>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {isSignUp ? (
                  <>
                    <div className="relative p-4 hidden md:block">
                      <div className="h-full">
                        {signupImage && (
                          <img
                            alt={signupImage.alt}
                            src={signupImage.src}
                            className="md:h-full w-full md:object-contain h-[130px] object-fill"
                          />
                        )}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conditionally render the PhoneNumberModal */}
      {showPhoneModal && (
        <PhoneNumberModal onClose={() => setShowPhoneModal(false)} />
      )}

      {toast.showToast && (
        <div
          className={`fixed top-4 right-4 p-4 z-50 rounded shadow w-[250px] ${
            toast.type === "success"
              ? "bg-green-50 text-green-600"
              : "bg-red-50 text-red-600"
          }`}
        >
          <div className="flex">{toast.message}</div>
        </div>
      )}
    </>
  );
};

export default Header;
